import classNames from "classnames";
import classes from "./PreferredProductTile.module.less";
import { ProductContext } from "~/feature/Product/ProductContext";
import { Card } from "~/Configurators/Linear/components/Card";
import { Product } from "~/feature/Product";
import { IconSize } from "~/common/components/icon/IconSize";
import { NetPrice } from "~/common/components/Price/v2/NetPrice";
import { productHasInStock } from "~/feature/ProductDescriptor/v1/productDescriptorUtils";
import { INVALID_PACKAGE_CODE } from "~/utils/constants";
import { SimpleStockIndicator } from "~/common/components/StockIndicator/StockIndicator";
import { ProductComparisonCheckbox } from "~/ProductComparison/ProductComparisonCheckbox";
import { Button } from "~/common/components/Button/Button";
import { type MouseEvent, useMemo, useState } from "react";
import { Collapse, Stack } from "@mui/material";
import { SideButton } from "~/common/components/Button/SideButton";
import { IconType } from "~/common/components/icon/IconType";
import { useAddProductToWishlistFlow } from "~/common/hooks/useAddProductToWishlistFlow";
import { AddToCartService } from "~/AddToCart/AddToCartService";
import { Icon } from "~/common/components/icon/Icon";
import { useTranslation } from "react-i18next";
import { useSalesHelper } from "~/SalesCampaign/salesHelper";
import { SaleTag } from "~/SalesCampaign/components/SaleTag/SaleTag";
import { getBothProductPrices } from "~/utils/v2/priceSelectHelper";
import type { ProductDescriptor } from "~/feature/ProductDescriptor/v1/types";
import { usePurchasePlace } from "~/feature/Analytics/ga/recommendations/RecommendationContexts";
import type { PurchaseType } from "~/feature/Analytics/ga/recommendations/types";
import { renderButtons } from "~/feature/SaleRecommendations/useRecommendationTileButtons";

interface Props {
  className?: string;
  product: ProductDescriptor;
}

export const PreferredProductTile = (props: Props) => {
  const { t } = useTranslation();
  const { currentPrice: netPrice, currentPriceType } = getBothProductPrices({
    product: props.product,
    packCode: INVALID_PACKAGE_CODE,
    totalQuantity: 1,
  });
  const inStock = productHasInStock(props.product);

  const [expanded, setExpanded] = useState(false);

  const expand = () => setExpanded(true);
  const shrink = () => setExpanded(false);

  const [addToWishlist] = useAddProductToWishlistFlow({
    productInput: props.product.base,
  });

  const addToWishlistCb = () => addToWishlist();

  const recommendationPlace = usePurchasePlace();
  const recommendationType: PurchaseType = "ppr";

  const onAddToCartClick = useMemo(() => {
    return async (e: MouseEvent<HTMLButtonElement>) => {
      await AddToCartService.openAddToCartWizard(
        {
          productCode: props.product.base.code,
          restrictionQuery: "",
        },
        {
          page: recommendationPlace,
          recommendationPlace,
          recommendationType,
        },
      );
    };
  }, [recommendationPlace, recommendationType, props.product]);

  const showSaleTag = useSalesHelper(props.product).isProductOnSale();

  const buttons = renderButtons(props.product, {
    atc: {
      onClick: onAddToCartClick,
      // @ts-expect-error tracking attributes
      "tracking-aux-ppr-add": "true",
    },
    wishlist: {
      onClick: addToWishlistCb,
      // @ts-expect-error tracking attributes
      "tracking-aux-ppr-wishlist": "true",
      sizeVariant: "side",
    },
  });
  buttons.reverse();

  const renderCard = (canExpand: boolean) => (
    <Card
      disableHover={true}
      className={classNames(classes.card)}
      onMouseEnter={expand}
      onMouseLeave={shrink}
      tracking-aux-ppr-name={props.product.base.designation}
      tracking-aux-ppr-id={props.product.base.code}
    >
      <Product.PdpLink
        tracking-aux-ppr-name={props.product.base.designation}
        tracking-aux-ppr-id={props.product.base.code}
        reco_type={recommendationType}
      >
        <Product.PrimaryImageWithLogos clickable={false} className={classes.image} />
      </Product.PdpLink>
      {showSaleTag && <SaleTag className={classes.saleTag} size={32} />}
      <div className={classes.designationRow}>
        <Product.PreferredIndicator iconSize={IconSize.FONT_16} />
        <Product.PdpLink
          tracking-aux-ppr-name={props.product.base.designation}
          tracking-aux-ppr-id={props.product.base.code}
          reco_type={recommendationType}
        >
          <Product.Designation className={classes.designation} clickable={false} />
        </Product.PdpLink>
      </div>
      <NetPrice
        price={netPrice}
        extraLabelsEnabled={false}
        classes={{
          price: classNames(classes.price, currentPriceType === "salesPrice" && "f-green"),
          root: classes.priceRoot,
        }}
      />
      <SimpleStockIndicator inStock={inStock} />
      <Collapse classes={{ wrapperInner: classes.extendedSection }} in={canExpand && expanded} timeout={300}>
        <ProductComparisonCheckbox gaLocation={"preferredProduct"} productCode={props.product.base.code} />
        {buttons.length > 0 && (
          <Stack direction="row" gap={2} sx={{ pb: 2 }}>
            {buttons}
          </Stack>
        )}
      </Collapse>
    </Card>
  );

  return (
    <ProductContext value={props.product.base}>
      <div className={classNames(props.className, classes.root)}>
        {renderCard(false)}
        <div className={classes.overlayCard}>{renderCard(true)}</div>
      </div>
    </ProductContext>
  );
};
